import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Search, Mail, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/currency";
import { format, parseISO, isValid } from "date-fns";

// Safe date formatting helper
const safeFormatDate = (dateString, formatStr = "MMM dd, yyyy") => {
  if (!dateString) return "N/A";
  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? format(parsed, formatStr) : "N/A";
  } catch {
    return "N/A";
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(
        customer =>
          customer.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (customer.contact_number && customer.contact_number.includes(searchTerm))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-6 w-6 text-primary" />
                Customer Database
              </CardTitle>
              <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard">
                Back to Dashboard
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-secondary p-4 rounded-md">
                <p className="text-sm text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-heading font-bold">{customers.length}</p>
              </div>
              <div className="bg-secondary p-4 rounded-md">
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-heading font-bold">
                  {customers.reduce((sum, c) => sum + c.order_count, 0)}
                </p>
              </div>
              <div className="bg-secondary p-4 rounded-md">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-heading font-bold text-green-600">
                  {formatCurrency(customers.reduce((sum, c) => sum + c.total_spent, 0))}
                </p>
              </div>
            </div>

            {/* Customer List */}
            <div className="space-y-4">
              {filteredCustomers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No customers found</p>
                </div>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <div
                    key={index}
                    className="border border-border rounded-md p-6 hover:shadow-md transition-shadow"
                    data-testid={`customer-card-${index}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Name</p>
                        <p className="font-heading font-bold text-lg">{customer.client_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Contact</p>
                        {customer.email && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                        {customer.contact_number && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3" />
                            <span>{customer.contact_number}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Orders</p>
                        <p className="font-semibold">{customer.order_count} orders</p>
                        <p className="text-xs text-muted-foreground">
                          Last: {safeFormatDate(customer.last_order_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                        <p className="font-heading font-bold text-lg text-green-600">
                          {formatCurrency(customer.total_spent)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
